import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import moment from 'moment';

export const bubEvents = {
  sessionTimeout: 1800, // 30 minutes
  sourceTimeout: 300, // 5 minutes
  pageVisitEventUuid: uuidv4(),
  userStoreData: {}, // We memoize this in userData

  trackingIndex(index) {
    index = _.isUndefined(index) ? 0 : index;
    return index + 1; // for events tracked by index position on page, start at 1 rather than 0
  },

  track(category, action, properties) {
    properties = _.isUndefined(properties) ? {} : properties;
    const data = this.trackingData(this.strip(category), this.strip(action), properties, 'mockingjay_event');
    const eventId = this.storeEvent(data);
    return this.postTracking(eventId, data);
  },

  strip(str) {
    if (typeof str === 'string' && typeof String.prototype.trim !== 'undefined') {
      return str.trim();
    }
    return str;
  },

  trackUsingBeacon(category, action, properties) {
    if (_.isEmpty(window.bubEventsProcessingUrl)) {
      return null;
    }
    properties = _.isUndefined(properties) ? {} : properties;
    const data = this.trackingData(category, action, properties, 'mockingjay_event');
    try {
      return navigator.sendBeacon(window.bubEventsProcessingUrl, JSON.stringify(data));
    } catch (e) {
      // [LP 11-01-18] Suppress error for now; we're exceeding rollbar usage
      // Browser support is improving and user % affected is still minimal relative.
      return this.track(category, action, properties);
    }
  },

  trackPageVisit(properties = {}) {
    const data = this.trackingData('Visit', null, properties, 'mockingjay_page_visit');
    const eventId = this.storeEvent(data);
    return this.postTracking(eventId, data);
  },

  trackMycroftTests() {
    const tests = _.get(this.userData(), 'mycroftTests') || [];

    _.forEach(tests, testName => {
      this.track('Mycroft', 'Assignment checked', {
        label: 'Experiment name',
        value: testName,
      });
    });
  },

  postTracking(eventId, data) {
    if (_.isEmpty(window.bubEventsProcessingUrl)) {
      return null;
    }
    return this.eventService(data)
      .then(() => {
        localStorage.removeItem(eventId);
      })
      .catch(e => {
        if (e.status === 0) {
          console.warn(`bubEvents: likely clipped by onunload: ${JSON.stringify(e)}`);
        } else {
          console.error(`bubEvents: unexpected error: ${JSON.stringify(e)}`);
        }
      });
  },

  eventService(data) {
    // do we need a "keep-alive" to deal with page transitions? https://css-tricks.com/send-an-http-request-on-page-exit/
    return axios.post(window.bubEventsProcessingUrl, data);
  },

  trackingData(eventCategory, action, properties, streamCategory) {
    const emailRefererData = this.emailRefererData();
    return {
      browser_timestamp: this.timestamp(),
      category: this.strip(streamCategory),
      client_event_uuid: this.getEventUuid(streamCategory),
      email_referer_fingerprint: emailRefererData.fingerprint,
      email_referer_id: emailRefererData.id,
      email_referer_type: emailRefererData.type,
      email_referer_user_id: emailRefererData.user_id,
      event_action: this.strip(action),
      event_category: this.strip(eventCategory),
      event_label: this.strip(_.get(properties, 'label')),
      event_value: this.strip(_.get(properties, 'value')),
      is_recognized: _.get(this.userData(), 'user.isRecognized'),
      is_tokened: _.get(this.userData(), 'user.isTokened'),
      page: this.page(),
      page_referer: _.get(properties, 'pageReferer') || this.pageReferer(),
      page_visit_event_uuid: this.getPageVisitUuid(),
      properties: _.omit(properties, ['label', 'value', 'pageReferer']),
      screensize: this.screensize(),
      session_id: this.fetchSessionId(),
      session_source: this.getSessionSource(),
      last_touch_session_source: this.getLastTouchSessionSource(),
      user_id: _.get(this.userData(), 'user.userId'),
      user_tracking_id: _.get(this.userData(), 'user.trackingId'),
    };
  },

  // SR: for page visits, returns a page-dependent uuid to store in 'client_event_uuid'.
  // This associates page visits to their client event(s), which have the same uuid in
  // 'page_visit_event_uuid'.
  getEventUuid(streamCategory) {
    if (streamCategory === 'mockingjay_page_visit') {
      return this.getPageVisitUuid();
    }
    return this.generateClientEventUuid();
  },

  getMid() {
    return _.get(this.userData(), 'user.mid');
  },

  getSessionSource() {
    return _.get(this.userData(), 'user.sessionSource');
  },

  parsedLastTouchSourceCookie() {
    const cookie = window.Cookies.get('bub_events_last_touch_source');
    return cookie ? JSON.parse(cookie) : {};
  },

  getLastTouchSessionSource() {
    return _.get(this.parsedLastTouchSourceCookie(), 'source');
  },

  emailRefererData() {
    const mid = this.getMid();
    const midParts = _.split(mid, '-', 4);
    return {
      type: midParts[0] || null,
      id: midParts[1] || null,
      user_id: midParts[2] || null,
      fingerprint: midParts[3] || null,
    };
  },

  userData() {
    const hasUserStoreData = $('#user-store-data').length > 0;

    if (hasUserStoreData && _.isEmpty(this.userStoreData)) {
      this.userStoreData = JSON.parse($('#user-store-data').attr('data'));
    }

    return this.userStoreData;
  },

  fetchSessionId() {
    this.checkSessionStatus();
    return window.Cookies.get('bub_events_session_id');
  },

  checkSessionStatus() {
    const sessionId = window.Cookies.get('bub_events_session_id');
    const activeSession =
      window.Cookies.get('bub_events_session_timestamp') &&
      this.sessionTime() - window.Cookies.get('bub_events_session_timestamp') < this.sessionTimeout * 1000;

    if (activeSession && sessionId) {
      return this.extendSession();
    }
    return this.renewSession();
  },

  extendSession() {
    window.Cookies.set('bub_events_session_timestamp', this.sessionTime(), { path: '/' });
  },

  clearStaleLastTouchSource() {
    const cookieRecordedAt = _.get(this.parsedLastTouchSourceCookie(), 'recorded_at_epoch_ms');

    if (!cookieRecordedAt) {
      return;
    }

    if (this.sessionTime() - cookieRecordedAt > this.sourceTimeout * 1000) {
      window.Cookies.set('bub_events_last_touch_source', '{}', { path: '/', expires: 0 });
    }
  },

  renewSession() {
    this.clearStaleLastTouchSource();

    window.Cookies.set('bub_events_session_timestamp', this.sessionTime(), { path: '/' });
    window.Cookies.set('bub_events_session_id', this.generateSessionId(), { path: '/' });
  },

  generateEventId() {
    return `bubEvent-${uuidv4()}`;
  },

  storeEvent(data) {
    const eventData = JSON.stringify(data);
    const eventId = this.generateEventId();

    if (this.supportsLocalStorage()) {
      try {
        localStorage.setItem(eventId, eventData);
      } catch (e) {
        // quotaExceededError
      }
    }
    return eventId;
  },

  drainEvents() {
    _(localStorage)
      .map((event, index) => localStorage.key(index))
      .filter(key => _.startsWith(key, 'bubEvent-'))
      .forEach(eventId => {
        const eventData = JSON.parse(localStorage.getItem(eventId));
        this.postTracking(eventId, eventData);
      });
  },

  supportsLocalStorage() {
    // http://diveintohtml5.info/detect.html#storage
    try {
      return 'localStorage' in window && window.localStorage !== null;
    } catch (e) {
      return false;
    }
  },

  // Helpers explicitly for sinon stubbing under test
  page() {
    return window.location.href;
  },

  pageQuery() {
    return window.location.search;
  },

  pageReferer() {
    return document.referrer;
  },

  getPageVisitUuid() {
    // Handle multiple instances of bubEvents and only use the pageVisitEventUuid from the instance
    // registered on the `window` as the singleton.  Fallback to local reference if there's not a
    // global singleton instance on the window.
    if (_.has(window.bubEvents, 'pageVisitEventUuid')) {
      return window.bubEvents.pageVisitEventUuid;
    }

    return this.pageVisitEventUuid;
  },

  screensize() {
    return window.innerWidth;
  },

  timestamp() {
    return moment().utc().format('ddd, DD MMM YYYY HH:mm:ss.SS z');
  },

  sessionTime() {
    return new Date().getTime();
  },

  generateSessionId() {
    return uuidv4();
  },

  generateClientEventUuid() {
    return uuidv4();
  },
};

document.addEventListener('click', () => {
  bubEvents.checkSessionStatus();
});

if (!document.addedContentLoadedListener) {
  document.addEventListener('DOMContentLoaded', () => bubEvents.drainEvents());
  document.addedContentLoadedListener = true;
}
